import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { showCustomToast } from '../common/CustomToast';
import VoucherModal from './VoucherModal';
import './DepositModal.css';

interface DepositModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type DepositView = 'methods' | 'solana';

const DepositModal: React.FC<DepositModalProps> = ({ isOpen, onClose }) => {
  const [currentView, setCurrentView] = useState<DepositView>('methods');
  const [isClosing, setIsClosing] = useState(false);
  const [isVoucherModalOpen, setIsVoucherModalOpen] = useState(false);
  const { user } = useSelector((state: RootState) => state.auth);

  if (!isOpen || !user) return null;

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(user.depositAddress);
    showCustomToast.success('Solana deposit address copied!', 3000);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      // Reset view when closing
      setTimeout(() => setCurrentView('methods'), 100);
    }, 300); // Wait for animation to complete
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div className={`deposit-modal-backdrop ${isClosing ? 'closing' : ''}`} onClick={handleBackdropClick}>
      <div className={`deposit-modal ${isClosing ? 'closing' : ''}`}>
        {currentView === 'methods' ? (
          <>
            <div className="deposit-modal-header">
              <h2>Deposit</h2>
              <button className="close-btn" onClick={handleClose}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="deposit-modal-body">
              <h3 className="method-title">Crypto Methods</h3>
              
              <button 
                className="crypto-method-btn"
                onClick={() => setCurrentView('solana')}
              >
                <div className="crypto-method-icon">
                  <svg width="24" height="24" viewBox="0 0 397.7 311.7" fill="currentColor">
                    <defs>
                      <linearGradient id="solanaGradient" x1="360.8791" y1="351.4553" x2="141.213" y2="-69.2936" gradientUnits="userSpaceOnUse">
                        <stop offset="0" stopColor="#00FFA3"/>
                        <stop offset="1" stopColor="#DC1FFF"/>
                      </linearGradient>
                      <linearGradient id="solanaGradient2" x1="264.8291" y1="401.6014" x2="45.163" y2="-19.1475" gradientUnits="userSpaceOnUse">
                        <stop offset="0" stopColor="#00FFA3"/>
                        <stop offset="1" stopColor="#DC1FFF"/>
                      </linearGradient>
                      <linearGradient id="solanaGradient3" x1="312.5484" y1="376.688" x2="92.8822" y2="-44.061" gradientUnits="userSpaceOnUse">
                        <stop offset="0" stopColor="#00FFA3"/>
                        <stop offset="1" stopColor="#DC1FFF"/>
                      </linearGradient>
                    </defs>
                    <path d="M64.6,237.9c2.4-2.4,5.7-3.8,9.2-3.8h317.4c5.8,0,8.7,7,4.6,11.1l-62.7,62.7c-2.4,2.4-5.7,3.8-9.2,3.8H6.5c-5.8,0-8.7-7-4.6-11.1L64.6,237.9z" fill="url(#solanaGradient)"/>
                    <path d="M64.6,3.8C67.1,1.4,70.4,0,73.8,0h317.4c5.8,0,8.7,7,4.6,11.1l-62.7,62.7c-2.4,2.4-5.7,3.8-9.2,3.8H6.5c-5.8,0-8.7-7-4.6-11.1L64.6,3.8z" fill="url(#solanaGradient2)"/>
                    <path d="M333.1,120.1c-2.4-2.4-5.7-3.8-9.2-3.8H6.5c-5.8,0-8.7,7-4.6,11.1l62.7,62.7c2.4,2.4,5.7,3.8,9.2,3.8h317.4c5.8,0,8.7-7,4.6-11.1L333.1,120.1z" fill="url(#solanaGradient3)"/>
                  </svg>
                </div>
                <div className="crypto-method-info">
                  <span className="crypto-name">Solana</span>
                  <span className="crypto-symbol">SOL</span>
                </div>
                <svg className="arrow-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>

              <div className="voucher-section">
                <span className="voucher-text">Got a voucher?</span>
                <button 
                  className="deposit-voucher-btn"
                  onClick={() => setIsVoucherModalOpen(true)}
                >
                  Redeem
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="deposit-modal-header">
              <button className="back-btn" onClick={() => setCurrentView('methods')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
                Back to methods
              </button>
              <button className="close-btn" onClick={handleClose}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="deposit-modal-body">
              <h3 className="network-title">Solana Network</h3>
              <p className="network-description">
                You will receive your balance after sending SOL to the address shown below:
              </p>

              <div className="address-section">
                <label className="address-label">SOL Address</label>
                <div className="address-input-wrapper">
                  <div className="address-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={user.depositAddress}
                    readOnly
                    className="address-input"
                  />
                  <button className="copy-btn" onClick={handleCopyAddress} title="Copy address">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                  </button>
                </div>
              </div>


            </div>
          </>
        )}
      </div>
      
      <VoucherModal 
        isOpen={isVoucherModalOpen} 
        onClose={() => setIsVoucherModalOpen(false)} 
      />
    </div>
  );
};

export default DepositModal;
